import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Clock } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { GUIDES } from "@/data/guides-loader";

const GuiasPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>Guías de Marketing Digital para PYMEs | Vibra Marketing</title>
        <meta
          name="description"
          content="Guías pilar de marketing digital, SEO, publicidad y web para PYMEs en España. Recursos prácticos, actualizados y escritos por el equipo de Vibra Marketing."
        />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={`${SITE.url}/guias`} />
        <link rel="alternate" hrefLang="es-ES" href={`${SITE.url}/guias`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content="Guías de Marketing Digital para PYMEs | Vibra Marketing" />
        <meta
          property="og:description"
          content="Guías pilar de marketing digital, SEO, publicidad y web para PYMEs en España."
        />
        <meta property="og:url" content={`${SITE.url}/guias`} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbForPage([
              { name: "Inicio", path: "/" },
              { name: "Guías", path: "/guias" },
            ]),
          )}
        </script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <Badge variant="secondary" className="mb-4 gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Guías Vibra
            </Badge>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-[1.05] mb-5">
              Guías para crecer{" "}
              <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                tu negocio
              </span>
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl">
              Recursos completos sobre marketing digital, SEO, publicidad y web.{" "}
              <span className="text-foreground font-medium">
                Escritos por el equipo que ejecuta cada servicio
              </span>
              . Sin teoría de manual.
            </p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative py-8 md:py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={`/guias/${g.slug}`}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 md:p-7 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="px-2.5 py-1 rounded-full border border-primary/20 bg-primary/10 text-primary text-[11px] font-medium">
                    {g.serviceTitle}
                  </span>
                  {g.readingTime ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {g.readingTime} min
                    </span>
                  ) : null}
                </div>

                <h3 className="font-semibold text-lg md:text-xl leading-snug mb-3 group-hover:text-primary transition-colors">
                  {g.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  {g.summary}
                </p>

                <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">
                  Leer guía <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuiasPage;
